import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import AsideBar from "../components/AsideBar";
import LevelFilters from "../components/LevelFilters";
import MainContent from "../components/MainContent";
import Icons from "../components/Icons";
import moment from "moment";
import { clog } from "../lib/utils";

export default function PersonLevelID() {
  const navigate = useNavigate();
  const [showFilters, setShowFilters] = useState(true);
  const [activeTab, setActiveTab] = useState("personal");
  const [filterInfo, setFilterInfo] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState(() => {
    const start = searchParams.get("lastVisitDate_start");
    const end = searchParams.get("lastVisitDate_end");
    const identityParam = searchParams.get("identityLevel");

    // Initialize filters object
    const initialFilters = {
      lastVisitDate: {
        start: start || moment().subtract(1, "months").format("YYYY-MM-DD"),
        end: end || moment().format("YYYY-MM-DD"),
      },
      identityLevel: identityParam ? identityParam.split("|||") : ["person"],
    };

    // Read other filter parameters from URL
    const otherFilterKeys = [
      // "visitorLocation",
      // "designationTitle",
      // "tags",
      // "companyHQ",
      "industry",
      "sector",
      // "icpScore",
      // "numberOfVisits",
      // "revenue",
      // "employees",

      //new or edited filters
      "visitorLocation",
      "industries",
      "sectors",
      "exactVisitor",
      "tech",
      "leadscore",
      "hasPhoneNumber",
      "revenue",
    ];

    otherFilterKeys.forEach((key) => {
      const paramValue = searchParams.get(key);
      if (paramValue) {
        // Use ||| as delimiter to allow commas in values
        initialFilters[key] = paramValue.split("|||");
      }

      // Handle slider ranges
      const minValue = searchParams.get(`${key}_min`);
      const maxValue = searchParams.get(`${key}_max`);
      if (minValue && maxValue) {
        initialFilters[key] = [parseInt(minValue), parseInt(maxValue)];
      }
    });

    return initialFilters;
  });

  const handleFilterChange = (newFilters) => {
    clog("PersonLevelID handleFilterChange called with:", newFilters);

    // Check if this is a "clear" operation
    const isClearOperation = newFilters._clearSliders === true;

    // Remove the flag before processing
    const { _clearSliders, ...filtersWithoutFlag } = newFilters;

    // Ensure identityLevel is always an array
    const identityArr = Array.isArray(filtersWithoutFlag.identityLevel)
      ? filtersWithoutFlag.identityLevel
      : [filtersWithoutFlag.identityLevel];
    const normalized = { ...filtersWithoutFlag, identityLevel: identityArr };
    setFilters(normalized);

    const params = new URLSearchParams();

    // Handle lastVisitDate
    if (normalized.lastVisitDate) {
      params.set("lastVisitDate_start", normalized.lastVisitDate.start);
      params.set("lastVisitDate_end", normalized.lastVisitDate.end);
    }

    // Handle identityLevel
    if (identityArr.length) {
      params.set("identityLevel", identityArr.join("|||"));
    }

    // Handle all other filter parameters
    Object.keys(normalized).forEach((key) => {
      if (key !== "lastVisitDate" && key !== "identityLevel") {
        const value = normalized[key];
        if (
          Array.isArray(value) &&
          value.length > 0 &&
          typeof value[0] === "string"
        ) {
          // Handle dropdown arrays (strings) - use ||| as delimiter to allow commas in values
          params.set(key, value.join("|||"));
        } else if (
          Array.isArray(value) &&
          value.length === 2 &&
          typeof value[0] === "number" &&
          !isClearOperation
        ) {
          // Handle slider ranges [min, max]
          // Only add if NOT a clear operation
          params.set(`${key}_min`, value[0]);
          params.set(`${key}_max`, value[1]);
        } else if (value == "true" || value == "false") {
          params.set(key, value);
        }
      }
    });

    clog("Setting search params:", params.toString());
    setSearchParams(params);

    // Navigate if identity level toggled to Company
    if (identityArr.includes("company")) {
      navigate({ pathname: "/company-level-id", search: params.toString() });
    }
  };

  const handleFilterInfoChange = (filterinfo) => {
    setFilterInfo(filterinfo);
  };

  // On mount, ensure identity level matches page and all defaults are in URL
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;

    // Ensure identity level is correct for this page
    if (!filters.identityLevel?.includes("person")) {
      setFilters((prev) => ({
        ...prev,
        identityLevel: ["person"],
      }));
      params.set("identityLevel", "person");
      needsUpdate = true;
    } else if (!params.has("identityLevel")) {
      // Identity level is correct in state but missing from URL
      params.set("identityLevel", filters.identityLevel.join(","));
      needsUpdate = true;
    }

    // Ensure lastVisitDate defaults are in URL if not present
    if (
      !params.has("lastVisitDate_start") ||
      !params.has("lastVisitDate_end")
    ) {
      params.set("lastVisitDate_start", filters.lastVisitDate.start);
      params.set("lastVisitDate_end", filters.lastVisitDate.end);
      needsUpdate = true;
    }

    // Only update URL if there were missing params
    if (needsUpdate) {
      setSearchParams(params, { replace: true });
    }
  }, []);

  return (
    <div className=" flex">
      <LevelFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        filterInfo={filterInfo}
        isVisible={true}
        onClose={() => setShowFilters(false)}
      />

      {/* Main Content Section */}
      <div className="flex-1 relative ">
        <MainContent
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          filters={filters}
          onFilterInfoChange={handleFilterInfoChange}
        />
      </div>
    </div>
  );
}
